<template>
    <!-- Telephone input field -->
    <div :class="telephoneInputClass">
        <label for="phone" class="input__label" :class="{ 'required-input': mandatory }" v-text="label || $t('TELEPHONE_FIELD_PHONE_NUMBER')"></label>
        <input name="phone" autocomplete="tel" type="tel" class="input__field" v-model.lazy="telephone" id="phone">

        <!-- Validation -->
        <p class="input__message" v-if="showRequiredValidationMessage"
            v-html="$t('TELEPHONE_FIELD_PLEASE_PUT')"></p>
        <p class="input__message" v-if="showIsUniqueValidationMessage">
            <span class="block" v-html="$t('TELEPHONE_FIELD_WE_ALREADY')"></span>
            <span class="mt-3 block">
                {{ $t('TELEPHONE_FIELD_COULD_YOU') }}
                <router-link :to="{ name: 'login' }" class='link text-xs'>
                    {{ $t('TELEPHONE_FIELD_LOG_IN_HERE') }}
                </router-link>.
            </span>
        </p>
        <p class="input__message" v-if="showValidTelephoneValidationMessage"
            v-html="$t('TELEPHONE_FIELD_SORRY')"></p>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import required from 'vuelidate/src/validators/required';
import { telephoneNumberInternationlFormat } from '@/helpers/TelephoneHelpers';
import { isTelephoneUnique, isValidTelephone } from '@/validators/TelephoneValidator';

export default {
    name: 'TelephoneField',
    props: {
        requireUnique: {
            default: false,
            type: Boolean,
        },
        value: {
            default: null,
            type: String,
        },
        label: {
            default: null,
            type: String,
        },
        fullWidth: {
            default: false,
            type: Boolean,
        },
        mandatory: {
            default: false,
            type: Boolean,
        },
    },
    data() {
        return {
            apiValidation: {
                telephone: {
                    isTelephoneUnique: true,
                },
            },
            telephone: null,
            selectedLabel: null,
        };
    },
    computed: {
        canValidateTelephoneUnique() {
            return this.requireUnique && this.$v.telephone.isValidTelephone && this.$v.telephone.required;
        },

        isTelephoneValid() {
            return !this.$v.$invalid;
        },

        showIsUniqueValidationMessage() {
            if (this.showValidTelephoneValidationMessage) return false;

            return !this.$v.telephone.isTelephoneUnique;
        },

        showRequiredValidationMessage() {
            return this.telephone !== null && !this.$v.telephone.required;
        },

        showValidTelephoneValidationMessage() {
            if (this.showRequiredValidationMessage) return false;

            return !this.$v.telephone.isValidTelephone;
        },

        telephoneInputClass() {
            if (this.telephone === null) return this.fullWidth ? 'input remove-padding' : 'input';

            if (this.isTelephoneValid) {
                return this.fullWidth ? 'input input--success remove-padding' : 'input input--success';
            }
            return this.fullWidth ? 'input input--invalid remove-padding' : 'input input--invalid';
        },
    },
    mounted() {
        this.telephone = this.value;
    },
    methods: {
        /**
         * Check if another user hasn't registered with the requested telephone number.
         *
         * @param {String} telephone
         *
         * @returns {void}
         */
        async isTelephoneAvailable(telephone) {
            let status = await isTelephoneUnique(telephone);

            this.updateApiValidationStatus(status);
            this.updateInput();
        },

        /**
         * Update telephone api validation status.
         *
         * @param {Boolean} status
         *
         * @returns {void}
         */
        updateApiValidationStatus(status) {
            this.apiValidation.telephone.isTelephoneUnique = status;
        },

        /**
         * Sync with parent component.
         *
         * @returns {void}
         */
        updateInput() {
            this.$v.$touch();

            this.$emit('input', this.telephone);
            this.$emit('update:validation', !this.$v.$invalid);
        },
    },
    validations: {
        telephone: {
            required,
            isValidTelephone() {
                return this.telephone !== null ? isValidTelephone(this.telephone) : true;
            },
            isTelephoneUnique() {
                return this.apiValidation.telephone.isTelephoneUnique;
            },
        },
    },
    watch: {
        telephone: debounce(function(value) {
            this.telephone = telephoneNumberInternationlFormat(value);

            this.canValidateTelephoneUnique ? this.isTelephoneAvailable(value) : this.updateInput();
        }, 500),
        value(newValue) {
            if (newValue !== this.telephone) {
                this.telephone = newValue;
            }
        },
    },
};
</script>

<style>
    .remove-padding {
        padding-left: 0px;
        padding-right: 0px;
    }
</style>
